import React, { useState } from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Award, Ship, Factory, Globe2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import MillTour from '../components/MillTour';
import { management, markets, stats, certifications, contact } from '../mock/mock';

const initials = (name) => name.split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

const ManagementPhoto = ({ member }) => {
  const [failed, setFailed] = useState(false);
  if (failed || !member.image) {
    return (
      <div data-testid="management-photo-fallback" className="w-full h-full grid place-items-center bg-ktc-green">
        <span className="font-serif text-5xl md:text-6xl text-ktc-goldLight tracking-wide">{initials(member.name)}</span>
      </div>
    );
  }
  return (
    <img
      src={member.image}
      alt={member.name}
      onError={() => setFailed(true)}
      loading="lazy"
      className="w-full h-full object-cover object-top"
    />
  );
};

const About = () => {
  return (
    <div>
      <Seo title="About KTC — Leading Rice Exporters in Pakistan Since 1999" description="Learn about Kassam Trading Company: a REAP-registered, family-run rice miller and exporter from Karachi, Pakistan with our own mill at Port Qasim, serving 30+ export markets worldwide." path="/about" />
      <PageHero
        title="Your Trusted Rice & Salt Exporter from Pakistan Since 1999"
        subtitle="A REAP-registered rice miller and exporter operating its own processing plant at Port Qasim, Karachi."
        crumb="About KTC"
      />

      {/* Intro */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal className="img-zoom rounded-2xl overflow-hidden shadow-xl">
          <img src="/loading.jpg" alt="KTC facility" className="w-full h-[430px] object-cover" />
        </Reveal>
        <Reveal delay={100}>
          <span className="eyebrow">Company History</span>
          <h2 className="font-serif text-4xl font-semibold text-ktc-green mt-3">Rice Miller & Exporter, Karachi</h2>
          <div className="space-y-4 mt-5 text-ktc-charcoal/75 leading-relaxed text-[15px]">
            <p>Kassam Trading Company is a trusted rice miller and exporter that buyers have relied on since 1999. We operate our own processing plant at Port Qasim, Karachi, giving us full control over milling, packing and export timelines.</p>
            <p>Our well-established global network enables us to efficiently handle logistics and deliver rice promptly to clients across the UAE, East Africa, West Africa, the Gulf, Southeast Asia and Europe. We are proud members of the Rice Exporters Association of Pakistan (REAP).</p>
            <p>Commitment to our customers is the cornerstone of our business. We value long-term relationships and provide personalised service, tailor-made solutions and competitive pricing.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
            {stats.map((s) => (
              <div key={s.label} className="bg-ktc-cream/60 rounded-lg p-4 text-center">
                <div className="font-serif text-2xl font-semibold text-ktc-green">{s.value}</div>
                <div className="text-[11px] uppercase tracking-wide text-ktc-charcoal/60 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Mission / Vision / Reputation */}
      <section className="bg-ktc-cream/60">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-6">
          {[
            { icon: Target, title: 'Our Mission', text: 'To be the most reliable and efficient rice producer and exporter in Pakistan and around the world. We are committed to grow in a consistent and sustainable manner.' },
            { icon: Eye, title: 'Our Vision', text: 'To be of the standard of excellence. We aim to be the best in the industry in terms of quality, price and customer service, setting a higher benchmark for the industry.' },
            { icon: Award, title: 'High Reputation', text: 'The company has built a high reputation as a rice miller, processor and exporter in Pakistan, trusted across 30+ countries.' },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 80} className="bg-ktc-ivoryLight rounded-xl p-8 border border-ktc-cream card-lift">
              <div className="h-12 w-12 grid place-items-center rounded-lg bg-ktc-green text-ktc-goldLight mb-5">
                <c.icon size={22} />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-ktc-green">{c.title}</h3>
              <p className="text-sm text-ktc-charcoal/70 mt-3 leading-relaxed">{c.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Management */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <span className="eyebrow">Leadership</span>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-ktc-green mt-3 gold-underline center inline-block pb-1">Meet the Management</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {management.map((m, i) => (
            <Reveal key={m.name} delay={i * 100} className="bg-ktc-ivoryLight rounded-xl overflow-hidden border border-ktc-cream card-lift">
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-2/5 h-72 sm:h-auto sm:min-h-[17rem] img-zoom">
                  <ManagementPhoto member={m} />
                </div>
                <div className="p-6 sm:w-3/5">
                  <h3 className="font-serif text-2xl font-semibold text-ktc-green">{m.name}</h3>
                  <div className="text-ktc-gold text-xs font-semibold uppercase tracking-wider mt-1">{m.role}</div>
                  <p className="text-sm text-ktc-charcoal/70 mt-3 leading-relaxed">{m.bio}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Mill Tour */}
      <section className="bg-ktc-cream/60">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">Processing Plant</span>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-ktc-green mt-3 gold-underline center inline-block pb-1">Take a Mill Tour</h2>
            <p className="text-ktc-charcoal/70 mt-8">From paddy intake to export loading at Port Qasim, explore every stage of our own processing plant.</p>
          </Reveal>
          <Reveal delay={80}>
            <MillTour />
          </Reveal>
        </div>
      </section>

      {/* Processing plant + global reach */}
      <section className="bg-ktc-green text-ktc-ivory relative overflow-hidden">
        <div className="absolute inset-0 grain-texture opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="eyebrow-light">Processing Plant & Global Reach</span>
            <h2 className="font-serif text-4xl font-semibold mt-3">Full Control, From Mill to Port</h2>
            <p className="text-ktc-ivory/75 mt-5 leading-relaxed">Our rice mill and processing facility at Port Qasim gives us end-to-end control over quality, packing and delivery. We ship FOB Port Qasim with CIF pricing available to all major ports worldwide.</p>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[{ icon: Factory, t: 'Own Mill' }, { icon: Ship, t: 'FOB Port Qasim' }, { icon: Globe2, t: '30+ Countries' }].map((x) => (
                <div key={x.t} className="bg-ktc-greenDark/60 border border-ktc-ivory/10 rounded-lg p-4 flex flex-col items-center text-center gap-2">
                  <x.icon size={22} className="text-ktc-goldLight" />
                  <span className="text-sm text-ktc-ivory/85">{x.t}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-8">
              {markets.map((m) => (
                <span key={m} className="px-4 py-2 rounded-full border border-ktc-ivory/20 text-xs text-ktc-ivory/85">{m}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={100} className="img-zoom rounded-2xl overflow-hidden shadow-2xl">
            <img src="https://images.pexels.com/photos/20581299/pexels-photo-20581299.jpeg" alt="Global shipping" className="w-full h-[430px] object-cover" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-20 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl font-semibold text-ktc-green">Let's build a long-term partnership</h2>
          <p className="text-ktc-charcoal/70 mt-4">Reach our team for tailor-made solutions and competitive pricing.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/quote" className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-semibold px-8 py-3.5 rounded-md transition-colors">Request a Quote <ArrowRight size={18} /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-ktc-green/25 hover:border-ktc-green text-ktc-green font-medium px-8 py-3.5 rounded-md transition-colors">Contact Us</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
